// Basic
import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
// Components
import Page from '@/common-utils/components/structure/page/Page';
import RentalFilesTable from '@/app/modules/rentalFiles/components/tables/RentalFilesTable';
// Store
import useRentalFilesStore from '@/app/store/modules/rentalFiles';
// Common
import RouteUrls from '../enums/RouteUrls';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
import { extractQueriesFromUrl, sanitizeValue } from '@/common-utils/functions/restUtils';
import { findApplicationFromCode } from '@/app/modules/contracts/enums/ItpRegex';
import remoteCollectionFields from '@/app/modules/remoteCollections/fields/remoteCollectionFields';

const RentalFiles = () => {

    // URL Params
    const { remoteCollectionId } = useParams();
    const location = useLocation();
    const urlQueries = extractQueriesFromUrl(location.search);

    // Store
    const storeEditedRemoteCollection = useRentalFilesStore(s => s.storeEditedRemoteCollection);
    const getEditedRemoteCollection = useRentalFilesStore(s => s.getEditedRemoteCollection);
    const storedEditedRemoteCollection = getEditedRemoteCollection(remoteCollectionId);

    // Constants
    const applicationCode = urlQueries[remoteCollectionFields.APPLICATION.key]
        ?? storedEditedRemoteCollection?.applicationCode ?? null;
    const application = applicationCode ? findApplicationFromCode(applicationCode)?.name : '';
    const contract = urlQueries[remoteCollectionFields.CONTRACT.key]
        ?? storedEditedRemoteCollection?.contract ?? null;
    const reconciliationNumber = urlQueries[remoteCollectionFields.RECONCILIATION_NUMBER.key]
        ?? storedEditedRemoteCollection?.reconciliationNumber ?? null;

    // Hooks
    useEffect(() => {
        if (remoteCollectionId && (reconciliationNumber || contract)) {
            storeEditedRemoteCollection(remoteCollectionId, {
                reconciliationNumber,
                contract,
                applicationCode
            });
        }
    }, [remoteCollectionId, reconciliationNumber, contract, applicationCode, storeEditedRemoteCollection]);

    // Constants
    const urlParams = Object.fromEntries(
        Object.entries({
            [remoteCollectionFields.RECONCILIATION_NUMBER.key]: reconciliationNumber,
            [remoteCollectionFields.CONTRACT.key]: contract,
            [remoteCollectionFields.APPLICATION.key]: applicationCode
        })
            .map(([key, value]) => [key, sanitizeValue(value)])
            .filter(([, value]) => value !== null && value !== '')
    );
    const i18nData = {
        number: contract,
        reconciliationNumber,
        application
    };

    return (
        <Page>
            <Page.Header
                route={RouteUrls.RENTAL_FILES}
                i18nData={i18nData}
                ids={{ remoteCollectionId }}
                routeBase={RouteUrls.REMOTE_COLLECTION}
            />
            <Page.Body data-cy='page-rental-files'>
                <RentalFilesTable
                    filtersEventName={setFiltersEventName('rental_files')}
                    urlParams={urlParams}
                />
            </Page.Body>
        </Page>
    );
};

export default RentalFiles;
